import { AHThemeType, useTheme } from "./AHTheme/AHTheme";
import { ActivityIndicator, Insets, StyleSheet, View } from "react-native";
import React, { PropsWithChildren, useMemo } from "react";

import { AHText } from "./Text/AHText";
import { addBreadcrumb } from "@sentry/react-native";

import { TouchableBox } from "./AHTheme/Box";
import { useLayoutContext } from "./LayoutProvider";
import { AHIcon, AHIconType } from "./AHIcon";

type AHButtonTypes = "default" | "small";
type AHButtonThemes = "default" | "primary" | "negative" | "plain";

const getButtonTheme = (
  themeType: AHButtonThemes | undefined,
  theme: AHThemeType
) => {
  switch (themeType) {
    case "plain":
      return {
        container: {
          backgroundColor: "#FFF",
        },
        label: {
          color: "darkerText" as const,
        },
      };
    case "primary":
      return {
        container: {
          backgroundColor: theme.primary,
        },
        label: {
          color: "lightText" as const,
        },
      };
    case "negative":
      return {
        container: {
          backgroundColor: theme.negative,
        },
        label: {
          color: "lightText" as const,
        },
      };
    default:
      return {
        container: {
          backgroundColor: theme.background,
        },
        label: {
          color: "darkerText" as const,
        },
      };
  }
};

function useButtonTypeStyle() {
  const layout = useLayoutContext();
  return useMemo(
    () => ({
      small: {
        borderRadius: layout.height.button / 2,
        width: layout.height.button,
        height: layout.height.button,
      },
      default: {
        borderRadius: layout.height.button / 2,
        paddingHorizontal: layout.space.large,
        height: layout.height.button,
      },
    }),
    [layout.height.button, layout.space.large]
  );
}

export type AHButtonProps = PropsWithChildren<{
  onPress: () => void;
  disabled?: boolean;
  themeType?: AHButtonThemes;
  type?: AHButtonTypes;
  iconType?: AHIconType;
  loadingTitle?: string;
  title?: React.ReactNode;
  style?: any;
  loading?: boolean;
  hideIfDisabled?: boolean;
  iconLeft?: boolean;
  iconRight?: boolean;
  labelStyle?: any;
  hitSlop?: Insets;
  status?: {
    type: any;
    message?: string;
  };
  ref?: React.RefObject<View | null>;
}>;

export const AHButton = ({
  type = "default",
  ref,
  ...props
}: AHButtonProps) => {
  const theme = useTheme();

  const buttonTheme = getButtonTheme(props.themeType, theme);
  const buttonTypeStyle = useButtonTypeStyle();
  const typeStyle = buttonTypeStyle[type];
  const title = props.loading ? props.loadingTitle : props.title;
  const renderIcon = (
    buttonTheme: ReturnType<typeof getButtonTheme>,
    style: any
  ) => {
    if (!props.iconType || props.loading) {
      return null;
    }

    return (
      <View
        style={[
          {
            height: 20,
            width: 20,
            borderRadius: 10,

            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            paddingLeft: StyleSheet.hairlineWidth,
            paddingTop: StyleSheet.hairlineWidth,
          },
          style,
        ]}
      >
        <AHIcon
          size="medium"
          color={buttonTheme.label.color}
          type={props.iconType}
        />
      </View>
    );
  };
  return (
    <TouchableBox
      ref={ref}
      disabled={props.disabled ?? props.loading}
      spaceHorizontal="large"
      {...props}
      onPress={() => {
        props.onPress();
        addBreadcrumb({
          type: "ui",
          category: "ui.press",
        });
      }}
      style={[
        {
          opacity: props.disabled ? (props.hideIfDisabled ? 0.0 : 0.6) : 1,

          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
        },
        buttonTheme.container,
        typeStyle,
        props.style,
      ]}
    >
      {props.iconLeft
        ? renderIcon(buttonTheme, { marginRight: title ? 8 : 0 })
        : null}
      {title ? (
        <AHText
          numberOfLines={1}
          type="subtitle"
          color={buttonTheme.label.color}
        >
          {title}
        </AHText>
      ) : (
        props.children
      )}
      {props.iconRight
        ? renderIcon(buttonTheme, { marginLeft: title ? 8 : 0 })
        : null}
      {props.loading ? (
        <ActivityIndicator
          color={theme[buttonTheme.label.color]}
          animating={props.loading}
          hidesWhenStopped
          style={{ marginLeft: title ? 8 : 0 }}
        />
      ) : null}
    </TouchableBox>
  );
};
